import { useEffect, useMemo } from 'react'
import {
  MASTER_BUS_ID,
  selectCurrentBuses,
  selectCurrentProject,
  useSequencerStore,
} from '@/stores/sequencerStore'
import { useUIStore } from '@/stores/uiStore'
import { SpectrumAnalyzer } from './SpectrumAnalyzer'
import { SlicerEditor } from '@/routes/werkshop.tools.sampler'
import { InstrumentPluginView } from './InstrumentPluginView'
import { WamGui } from './WamGui'

/**
 * Full-pane plugin host — the Plugin tab's render slot.
 *
 * Resolution priority for which plugin UI to show:
 *
 *   1. User explicitly expanded a bus FX row → `uiStore.selectedPlugin`
 *      points at that effect, we render its dedicated full-pane UI
 *      (today: FFT Analyzer; LO-FI will plug in here later).
 *   2. The focused pad has a plugin worth editing in a big surface —
 *      sample-kind or instrument+sampler pads open the Slicer pinned
 *      to that pad's sample; instrument+non-sampler pads (Deep Sub /
 *      Drone / Pluck Pad) will open their full paramSchema UI in a
 *      follow-up PR.
 *   3. Fallback: the Master bus's seeded FFT Analyzer so the Plugin
 *      tab is never blank when the user opens it cold.
 *
 * Keeping every full-pane plugin surface HERE (instead of routing off
 * to standalone pages) matches the user's intent that the Plugin tab
 * is the app's universal "tell me more about this plugin" column.
 */
interface PluginPaneProps {
  /** Forwarded to the child plugin's full-pane component so it can
   *  scale to the full-screen modal when the tab row's `⤢` toggle is
   *  on. Not every child respects it today — Slicer ignores it and
   *  flows naturally to whatever height the container provides. */
  fullScreen?: boolean
}

export function PluginPane({ fullScreen = false }: PluginPaneProps = {}) {
  const selected = useUIStore(s => s.selectedPlugin)
  const setSelectedPlugin = useUIStore(s => s.setSelectedPlugin)
  const buses = useSequencerStore(selectCurrentBuses)
  const project = useSequencerStore(selectCurrentProject)
  const focusedPad = useSequencerStore(s => s.focusedPad)

  const effect = selected
    ? buses
        .find(b => b.id === selected.busId)
        ?.effects?.find(e => e.id === selected.effectId)
    : null

  // Master-bus FFT Analyzer — the seeded default. Always-present by
  // contract (v13 migration + createEmptyContainer, see PR #225).
  const masterFftEffect = useMemo(() => {
    const master = buses.find(b => b.id === MASTER_BUS_ID)
    return master?.effects?.find(e => e.pluginId === 'fft-analyzer') ?? null
  }, [buses])

  // Derive the focused-pad sample id, if any. Sample-kind pads have
  // `sampleId` directly; instrument+sampler pads carry it on the
  // content definition. Both shapes resolve to the same "here's the
  // sample this pad plays" for Slicer embedding.
  const focusedSampleId = useMemo(() => {
    if (!project) return null
    const pad = project.padDefs?.[focusedPad]
    if (!pad) return null
    if (pad.kind === 'sample') return pad.sampleId ?? null
    if (pad.kind === 'instrument' && pad.instrumentId === 'sampler') {
      return pad.sampleId ?? null
    }
    return null
  }, [project, focusedPad])

  // Non-sampler instrument pad? That's the full-paramSchema view.
  // Excludes sampler-instrument pads (they go through the slicer
  // branch above via `focusedSampleId`) and empty pads.
  const focusedInstrumentPad = useMemo(() => {
    if (!project) return false
    const pad = project.padDefs?.[focusedPad]
    if (!pad || pad.kind !== 'instrument') return false
    if (pad.instrumentId === 'sampler') return false
    return true
  }, [project, focusedPad])

  // When the user hasn't explicitly picked a bus plugin AND the
  // focused pad isn't one we have a full-pane UI for, auto-point at
  // the master FFT Analyzer so the tab defaults to something useful.
  // Explicit user picks (selectedPlugin) are preserved — this effect
  // only fires when `effect` is null.
  useEffect(() => {
    if (effect) return
    if (focusedSampleId) return // slicer will render instead
    if (focusedInstrumentPad) return // instrument view will render instead
    if (!masterFftEffect) return
    setSelectedPlugin({
      busId: MASTER_BUS_ID,
      effectId: masterFftEffect.id,
    })
  }, [
    effect,
    focusedSampleId,
    focusedInstrumentPad,
    masterFftEffect,
    setSelectedPlugin,
  ])

  // Priority 1: explicit bus-FX selection.
  if (selected && effect) {
    // WAM plugins render their own Custom-Element UI via WamGui.
    if (effect.pluginId?.startsWith('wam-')) {
      return <WamGui fullScreen={fullScreen} />
    }
    if (effect.pluginId === 'fft-analyzer') {
      return <SpectrumAnalyzer fullScreen={fullScreen} />
    }
    // Plugin selected but no dedicated UI — readable fallback.
    return (
      <div className="flex flex-1 min-h-[20rem] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-brd-subtle bg-surface-sunken/60 px-6 py-12 text-center">
        <p className="text-sm font-medium text-content-primary">
          No full-pane UI for this plugin
        </p>
        <p className="max-w-md text-xs text-content-secondary">
          {effect.pluginId ?? 'This effect'} doesn&apos;t ship a dedicated full
          view yet. Edit it from the bus FX chain instead.
        </p>
      </div>
    )
  }

  // Priority 2a: focused sample / sampler-instrument pad → Slicer.
  if (focusedSampleId) {
    return (
      <div className="flex flex-1 min-h-0 flex-col overflow-auto">
        <SlicerEditor sampleIdOverride={focusedSampleId} hideSamplePicker />
      </div>
    )
  }

  // Priority 2b: focused non-sampler instrument pad → full paramSchema.
  if (focusedInstrumentPad) {
    return (
      <InstrumentPluginView padIndex={focusedPad} fullScreen={fullScreen} />
    )
  }

  // Priority 3: master FFT default. The useEffect above flipped
  // `selected` to the master row; render the analyzer this frame so
  // there's no blank-state flash between renders.
  if (masterFftEffect) {
    return <SpectrumAnalyzer fullScreen={fullScreen} />
  }

  // Truly nothing to show — very rare (master FFT seed lives in v13
  // migration + createEmptyContainer so every container has one).
  return (
    <div className="flex flex-1 min-h-[20rem] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-brd-subtle bg-surface-sunken/60 px-6 py-12 text-center">
      <p className="text-sm font-medium text-content-primary">No plugin open</p>
      <p className="max-w-md text-xs text-content-secondary">
        Focus a pad with a sample or instrument, or expand a bus FX row, to
        populate this view.
      </p>
    </div>
  )
}
